import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import AuthForm from './components/AuthForm';
import Dashboard from './components/Dashboard';
import ApplicationForm from './components/ApplicationForm';
import Phase3Form from './components/Phase3Form';
import AdminDashboard from './components/AdminDashboard';
import './styles/App.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Wait for user to be fully authenticated
          const token = await user.getIdToken(true); // Force refresh to ensure valid token
          
          setUser(user);
          
          // SECURITY: Check custom claims first (more secure)
          const tokenResult = await user.getIdTokenResult();
          const customClaims = tokenResult.claims;
          
          if (customClaims.admin === true) {
            setIsAdmin(true);
          } else {
            // Fallback to Firestore check (for backward compatibility)
            const adminDoc = await getDoc(doc(db, 'admins', user.uid));
            setIsAdmin(adminDoc.exists() && adminDoc.data()?.role === 'admin');
          }
        } catch (error) {
          console.error('Error during authentication:', error);
          setUser(null);
          setIsAdmin(false);
        }
      } else {
        setUser(null);
        setIsAdmin(false);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <AuthForm />;
  }

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        {/* Navigation */}
        <nav className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex items-center">
                <h1 className="text-xl font-bold text-gray-900">
                  VB Accelerator
                </h1>
                {isAdmin && (
                  <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Admin
                  </span>
                )}
              </div>
              
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-700">
                  Welcome, {user.displayName || user.email}
                </span>
                
                {isAdmin && (
                  <button
                    onClick={() => window.location.href = '/admin'}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Admin Dashboard
                  </button>
                )}
                
                
                <button
                  onClick={handleSignOut}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <Routes>
          <Route 
            path="/" 
            element={<Dashboard isAdmin={isAdmin} />} 
          />
          <Route 
            path="/application" 
            element={<ApplicationForm />} 
          />
          <Route 
            path="/phase3" 
            element={<Phase3Form />} 
          />
          <Route 
            path="/admin" 
            element={isAdmin ? <AdminDashboard /> : <Navigate to="/" />} 
          />
          <Route 
            path="*" 
            element={<Navigate to="/" />} 
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;